import { Icon } from '@blueprintjs/core'
import bugsnag, { appVersion, ErrorBoundary as BugsnagErrorBoundary } from '../lib/bugsnag'
import { Popover } from './ui/popover'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import posthog from 'posthog-js'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { EmptyState } from './ui/empty-state'
import { Provider, useDispatch } from 'react-redux'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { RecoilRoot } from 'recoil'
import { applyMiddleware, compose, createStore } from 'redux'
import thunkMiddleware from 'redux-thunk'
import resolveConfig from 'tailwindcss/resolveConfig'
import { AdminRoute } from '../AdminRoute'
import { AdGroupDetailV5 } from '../pages/ad_group_detail_v5'
import { AdGroups } from '../pages/ad_groups'
import { AdminBulkActions } from '../pages/admin/bulk-actions'
import { PublicRecipes } from '../pages/admin/public_recipes'
import { AdminRecipes } from '../pages/admin/recipes'
import { RecipesTesting } from '../pages/admin/recipes-testing'
import { AdminProfileDetail } from '../pages/admin/updates/admin_profile_detail'
import { AdminUserDetail } from '../pages/admin/updates/admin_user_detail'
import { AdminUpdateDetails } from '../pages/admin/updates/update_details'
import { AdminUpdates } from '../pages/admin/updates/updates'
import { AdminUsers } from '../pages/admin/users'
import { AmazonPortfolios } from '../pages/amazon_portfolios'
import { AuditLogDetail } from '../pages/audit_log_detail'
import { AuditLogsTable } from '../pages/audit_logs'
import { BidRecommendations } from '../pages/bid_recommendations'
import { Billing } from '../pages/billing'
import { CopilotPage } from '../pages/CopilotPage'
import { AdSpendCredit } from '../pages/billing/ad-spend-credit'
import { Cards } from '../pages/billing/cards'
import { BulkActionLogs } from '../pages/bulk-action-logs'
import { CampaignDetailV5 } from '../pages/campaign_detail_v5'
import { ChatPage } from '../pages/ChatPage'
import { CampaignNegativeKeywords } from '../pages/campaign_negative_keywords'
import { Campaigns3 } from '../pages/campaigns3'
import { CampaignPlacements } from '../pages/campaigns_placements'
import { ChangeLog } from '../pages/change_log'
import { Dashboard } from '../pages/dashboard'
import { DryRun } from '../pages/dry_run'
import { ForgotPassword } from '../pages/forget_password'
import { CombinedHistory } from '../pages/history/history_page'
import { Integrations } from '../pages/integrations'
import { ApiKeys } from '../pages/api_keys'
import { CustomFields } from '../pages/custom_fields'
import { CustomFieldValues } from '../pages/custom_field_values'
import { Keys } from '../pages/keys'
import { KeywordDetailV5 } from '../pages/keyword_detail_v5'
import { KeywordRecommendations } from '../pages/keyword_recommendations'
import { Keywords } from '../pages/keywords'
import { Login } from '../pages/login'
import { ExportAsinsPage } from '../pages/merch/export_asins'
import { Product } from '../pages/mockups/product'
import { NegativeKeywords } from '../pages/negative_keywords'
import { NegativeTargets } from '../pages/negative_targets'
import { Onboard } from '../pages/onboard'
import { ProductAds } from '../pages/product_ads'
import { Products } from '../pages/products'
import { ProfileSettings } from '../pages/profile_settings'
import { DashboardProfiles } from '../pages/profiles'
import { Promotion } from '../pages/promotion'
import { Promotions } from '../pages/promotions'
import { RecipeDetail } from '../pages/recipe_detail'
import { RecipeUpgradeReview } from '../pages/recipe_upgrade_review'
import { Recipes } from '../pages/recipes'
import { Segments } from '../pages/segments'
import { Register } from '../pages/register'
import { ReportUpdates } from '../pages/report-updates'
import { ResetPassword } from '../pages/reset_password'
import { SearchTerms } from '../pages/search_terms'
import { Settings } from '../pages/settings'
import { SinkAuditLogs } from '../pages/sink/action-logs'
import { SinkFilter } from '../pages/sink/filter'
import { SinkNumeric } from '../pages/sink/numeric'
import { Sink } from '../pages/sink/sink'
import { Sink2 } from '../pages/sink/sink2'
import { SnapshotUpdates } from '../pages/snapshot-updates'
import { TargetDetailV5 } from '../pages/target_detail_v5'
import { Targets } from '../pages/targets'
import { NGrams } from '../pages/tools/ngrams'
import { QuickCreate } from '../pages/tools/quick_create'
import { Undo } from '../pages/undo'
import { Updates } from '../pages/updates'
import { userStore } from '../stores/user_store'
import tailwindConfig from './../../../tailwind.config.js'
import { rootReducer } from './../reducers/index'
import { Wrapper } from './AppWrapper'
import { NavManager, navStore } from './nav/Nav'
import { NavV2 } from './nav-v2/NavV2'
import { navV2Store } from './nav-v2/store'
import { NotFound } from './not_found/NotFound'
import { ProductOverview } from './tools/ProductOverview'
import { TopCallouts } from './TopCallouts'
import { useUser } from '../hooks/useUser'
import { useCommandPaletteHotkey } from '../hooks/useCommandPaletteHotkey'
import { CommandPalette } from './command-palette/CommandPalette'
import { ActiveFilterStoreProvider } from './command-palette/active-filter-store-context'
import { RecipesV2Quickstart } from '../pages/help/recipes_v2_quickstart'
import { LegacyApiDocumentationRedirect } from '../pages/help/api_documentation_redirect'
import { fetchUserIfNeeded } from '../actions/user'
import { isNavV2Enabled } from '../util/nav-version'
import { capturePosthogException } from '../lib/posthog-error-tracking'
import { UpdateAvailablePrompt } from './UpdateAvailablePrompt'
export const tailwind = resolveConfig(tailwindConfig)

const LegacyPromotionsRedirect = ({ dryRun = false }) => {
    const { promotion_id } = useParams()
    const location = useLocation()
    const suffix = promotion_id ? `/${promotion_id}${dryRun ? '/dry-run' : ''}` : ''

    return <Navigate to={`/smart-campaigns${suffix}${location.search}`} replace />
}

const composeEnhancers =
    typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
        ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
        : compose

const enhancer = composeEnhancers(applyMiddleware(thunkMiddleware))
const store = createStore(rootReducer, enhancer)

posthog.init('phc_jUe32ZY9j2H7fOJVRo1poDoaagW8rxc7QrHp1Gs5UGm', {
    api_host: 'https://us.i.posthog.com',
    person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
    capture_exceptions: true,
})
posthog.register({ app_release: appVersion })

export const OptimizedPopover = (props) => {
    const portalContainer = useSharedPortal()

    if (!portalContainer) {
        return null // or a loading state if needed
    }

    return <Popover {...props} portalContainer={portalContainer} />
}

export const useSharedPortal = () => {
    const [portalContainer, setPortalContainer] = useState(null)

    useEffect(() => {
        const container = document.getElementById('shared-popover-portal')
        if (container) {
            setPortalContainer(container)
        }
    }, [])

    return portalContainer
}

const SharedPortalContainer = () => {
    return <div id="shared-popover-portal" />
}

export const App = ({}) => {
    return (
        <Provider store={store}>
            <RecoilRoot>
                <BrowserRouter>
                    <Wrapper>
                        <Toaster />
                        <AppContent />
                        <UpdateAvailablePrompt />
                        <SharedPortalContainer />
                    </Wrapper>
                </BrowserRouter>
            </RecoilRoot>
        </Provider>
    )
}

const AppContent = () => {
    const { pathname, search } = useLocation()
    const dispatch = useDispatch()
    const { user } = useUser()
    const [calloutsCount, setCalloutsCount] = useState(0)
    const [paletteOpen, setPaletteOpen] = useState(false)
    const isOnboarding = pathname.startsWith('/onboard')

    const togglePalette = useCallback(() => setPaletteOpen((open) => !open), [])
    const closePalette = useCallback(() => setPaletteOpen(false), [])

    useCommandPaletteHotkey(togglePalette)

    // Nav v2 is the default; ?nav=v1 or ?nav=legacy mounts the legacy NavManager.
    const useNavV2 = useMemo(() => isNavV2Enabled(search), [search])

    useEffect(() => {
        dispatch(fetchUserIfNeeded())
    }, [])

    useEffect(() => {
        const handler = (e) => {
            const isSaveShortcut =
                (e.metaKey || e.ctrlKey) && (e.key?.toLowerCase() === 's' || e.code === 'KeyS')

            if (!isSaveShortcut) {
                return
            }

            console.info('[App] save shortcut reached app root', {
                pathname,
                key: e.key,
                code: e.code,
                metaKey: e.metaKey,
                ctrlKey: e.ctrlKey,
                target:
                    e.target instanceof HTMLElement
                        ? {
                              tagName: e.target.tagName,
                              type: e.target.getAttribute('type'),
                              name: e.target.getAttribute('name'),
                              ariaLabel: e.target.getAttribute('aria-label'),
                          }
                        : null,
            })
        }

        window.addEventListener('keydown', handler, { capture: true })
        return () => window.removeEventListener('keydown', handler, { capture: true })
    }, [pathname])

    return (
        <ActiveFilterStoreProvider>
            <div className="flex flex-col w-screen h-screen bg-white">
                <CommandPalette isOpen={paletteOpen} onClose={closePalette} />
                {/* Also render callouts on /onboard while impersonating: a keyless
                account force-redirects the admin to /onboard, and the (priority-0)
                impersonation callout is the only "Stop impersonating" escape hatch.
                Because it's priority 0, no other callouts leak onto onboarding. */}
                {(!isOnboarding || user?.impersonating) && (
                    <div className="w-full sticky top-0 z-10">
                        <TopCallouts setCalloutsCount={setCalloutsCount} />
                    </div>
                )}
                <div className="flex w-full flex-1 min-h-0 bg-white">
                    <AppContainer />
                    {!isOnboarding &&
                        (useNavV2 ? (
                            <NavV2 calloutsCount={calloutsCount} />
                        ) : (
                            <NavManager calloutsCount={calloutsCount} />
                        ))}
                </div>
            </div>
        </ActiveFilterStoreProvider>
    )
}

const AppContainer = observer(({}) => {
    const user = userStore.user
    const profileId = userStore.profileId
    const { pathname, search } = useLocation()
    const isOnboarding = pathname.startsWith('/onboard')
    // Match the nav-v2 slide tempo (100ms) when v2 is mounted, so the content
    // area's width animation stays in lock-step with the sidebar's slide.
    const useNavV2 = isNavV2Enabled(search)

    useEffect(() => {
        // Set initial user context for Bugsnag if user is available
        if (user?.id) {
            bugsnag.setUser(user.id.toString(), user.email, user.name)
        }
    }, [user])

    useEffect(() => {
        const handleGistReady = () => {
            window.gist.chat('hide')
        }

        document.addEventListener('gistReady', handleGistReady)
        document.addEventListener('messenger:closed', handleGistReady, false)

        return () => {
            document.removeEventListener('gistReady', handleGistReady)
            document.removeEventListener('messenger:closed', handleGistReady)
        }
    }, [])

    const { width, left } = useMemo(() => {
        if (isOnboarding || !user?.id || !navStore.open || !navStore.isPinned) {
            return {
                width: '100%',
                left: 0,
            }
        }

        const navWidth = useNavV2 ? navV2Store.widthStyle : '16em'
        return {
            width: `calc(100% - ${navWidth})`,
            left: navWidth,
        }
    }, [isOnboarding, navStore.open, user?.id, navStore.isPinned, useNavV2, navV2Store.widthPx])

    return (
        <div
            className={cx(
                'relative h-full min-h-0 flex-shrink overflow-hidden transition-all ease-out bg-white',
                useNavV2 ? 'duration-100' : 'duration-300 ease-in-out',
            )}
            style={{
                width: width,
                left: left,
            }}
        >
            <div className="h-full min-h-0" style={{ zIndex: 11 }}>
                <div className="h-full min-h-0">
                    <BugsnagErrorBoundary
                        FallbackComponent={ErrorFallback}
                        onError={(event) =>
                            capturePosthogException(event.originalError, {
                                pathname,
                                profileId,
                                release: appVersion,
                            })
                        }
                    >
                        <MerchJarRoutes />
                    </BugsnagErrorBoundary>
                </div>
            </div>
        </div>
    )
})

const ErrorFallback = () => {
    return (
        <EmptyState icon={<Icon icon="error" size={60} />} className="mt-20">
            <h1>Something went wrong.</h1>
            <div>The Merch Jar team has been notified.</div>
        </EmptyState>
    )
}

const CampaignDetailRoute = () => {
    const { campaign_id } = useParams()
    return <CampaignDetailV5 key={campaign_id} />
}

const AdGroupDetailRoute = () => {
    const { ad_group_id } = useParams()
    return <AdGroupDetailV5 key={ad_group_id} />
}

const MerchJarRoutes = ({}) => {
    return (
        <Routes>
            <Route path="/login">
                <Login />
            </Route>
            <Route path="/register">
                <Register />
            </Route>

            <Route path="/password/forget">
                <ForgotPassword />
            </Route>
            <Route path="/password/reset/:token" element={<ResetPassword />} />

            <Route exact path="/">
                <Dashboard />
            </Route>
            <Route
                path="/profiles-overview"
                element={<Navigate to="/ad-accounts-overview" replace />}
            />
            <Route path="/ad-accounts-overview">
                <DashboardProfiles />
            </Route>
            <Route path="/dashboard">
                <Dashboard />
            </Route>
            <Route path="/search-terms">
                <SearchTerms />
            </Route>
            <Route
                path="/unified/search-terms"
                element={
                    <React.Suspense fallback={null}>
                        {React.createElement(
                            React.lazy(() => import('./unified/UnifiedSearchTermsPage')),
                        )}
                    </React.Suspense>
                }
            />
            <Route path="/ad-groups/:ad_group_id">
                <AdGroupDetailRoute />
            </Route>
            <Route path="/ad-groups">
                <AdGroups />
            </Route>
            <Route
                path="/unified/ad-groups"
                element={
                    <React.Suspense fallback={null}>
                        {React.createElement(
                            React.lazy(() => import('./unified/UnifiedAdGroupsPage')),
                        )}
                    </React.Suspense>
                }
            />
            <Route path="/campaigns/:campaign_id">
                <CampaignDetailRoute />
            </Route>
            <Route path="/campaigns">
                <Campaigns3 />
            </Route>
            <Route
                path="/unified/campaigns"
                element={
                    <React.Suspense fallback={null}>
                        {React.createElement(
                            React.lazy(() => import('./unified/UnifiedCampaignsPage')),
                        )}
                    </React.Suspense>
                }
            />
            <Route path="/campaigns3">
                <Campaigns3 />
            </Route>
            <Route path="/placements">
                <CampaignPlacements />
            </Route>
            <Route
                path="/unified/placements"
                element={
                    <React.Suspense fallback={null}>
                        {React.createElement(
                            React.lazy(() => import('./unified/UnifiedPlacementsPage')),
                        )}
                    </React.Suspense>
                }
            />
            <Route path="/campaign-negative-keywords">
                <CampaignNegativeKeywords />
            </Route>
            <Route path="/products">
                <Products />
            </Route>
            <Route path="/keywords/:keyword_id">
                <KeywordDetailV5 />
            </Route>
            <Route path="/keywords">
                <Keywords />
            </Route>
            <Route
                path="/unified/keywords"
                element={
                    <React.Suspense fallback={null}>
                        {React.createElement(
                            React.lazy(() => import('./unified/UnifiedKeywordsPage')),
                        )}
                    </React.Suspense>
                }
            />
            <Route path="/negative-keywords">
                <NegativeKeywords />
            </Route>
            <Route path="/product-ads">
                <ProductAds />
            </Route>
            <Route
                path="/unified/ads"
                element={
                    <React.Suspense fallback={null}>
                        {React.createElement(React.lazy(() => import('./unified/UnifiedAdsPage')))}
                    </React.Suspense>
                }
            />
            <Route
                path="/unified/products"
                element={
                    <React.Suspense fallback={null}>
                        {React.createElement(
                            React.lazy(() => import('./unified/UnifiedProductsPage')),
                        )}
                    </React.Suspense>
                }
            />
            <Route path="/targets/:target_id">
                <TargetDetailV5 />
            </Route>
            <Route path="/targets">
                <Targets />
            </Route>
            <Route
                path="/unified/targets"
                element={
                    <React.Suspense fallback={null}>
                        {React.createElement(
                            React.lazy(() => import('./unified/UnifiedTargetsPage')),
                        )}
                    </React.Suspense>
                }
            />
            <Route path="/negative-targets">
                <NegativeTargets />
            </Route>
            <Route path="/portfolios">
                <AmazonPortfolios />
            </Route>
            <Route path="/smart-campaigns/:promotion_id/dry-run">
                <DryRun />
            </Route>
            <Route path="/smart-campaigns/:promotion_id">
                <Promotion />
            </Route>
            <Route path="/smart-campaigns">
                <Promotions />
            </Route>
            <Route path="/promotions/:promotion_id/dry-run">
                <LegacyPromotionsRedirect dryRun />
            </Route>
            <Route path="/promotions/:promotion_id">
                <LegacyPromotionsRedirect />
            </Route>
            <Route path="/promotions">
                <LegacyPromotionsRedirect />
            </Route>
            <Route path="/smart-campaigns/:promotion_id/dry-run">
                <DryRun />
            </Route>
            <Route path="/smart-campaigns/:promotion_id">
                <Promotion />
            </Route>
            <Route path="/smart-campaigns">
                <Promotions />
            </Route>
            <Route path="/logs">
                <Navigate to="/combined-history" replace />
            </Route>
            <Route path="/audit-logs/:log_id">
                <AuditLogDetail />
            </Route>
            <Route path="/audit-logs">
                <AuditLogsTable />
            </Route>
            <Route path="/combined-history">
                <CombinedHistory />
            </Route>
            <Route path="/bulk-action-logs">
                <BulkActionLogs />
            </Route>

            <Route path="/tools/keyword-recommendations">
                <KeywordRecommendations />
            </Route>
            <Route path="/tools/bid-recommendations">
                <BidRecommendations />
            </Route>
            <Route path="/tools/quick-create">
                <QuickCreate />
            </Route>
            <Route path="/tools/product-overview">
                <ProductOverview />
            </Route>
            <Route path="/tools/export-asins">
                <ExportAsinsPage />
            </Route>
            <Route path="/tools/ngrams">
                <NGrams />
            </Route>

            <Route path="/copilot">
                <CopilotPage />
            </Route>
            <Route path="/chat">
                <ChatPage />
            </Route>
            <Route path="/chat/:conversationId">
                <ChatPage />
            </Route>

            <Route path="/updates">
                <Updates />
            </Route>
            <Route path="/report-updates">
                <ReportUpdates />
            </Route>
            <Route path="/snapshot-updates">
                <SnapshotUpdates />
            </Route>

            <Route path="/settings">
                <Settings />
            </Route>
            <Route path="/profile-settings">
                <ProfileSettings />
            </Route>
            <Route path="/change-log">
                <ChangeLog />
            </Route>
            <Route path="/keys">
                <Keys />
            </Route>
            <Route path="/integrations">
                <Integrations />
            </Route>
            <Route path="/api-keys">
                <ApiKeys />
            </Route>
            <Route path="/custom-fields">
                <CustomFields />
            </Route>
            <Route path="/custom-fields/values">
                <CustomFieldValues />
            </Route>
            <Route path="/custom-fields/:entityType/:definitionId">
                <CustomFieldValues />
            </Route>
            <Route
                path="/custom-field-values"
                element={<Navigate to="/custom-fields/values" replace />}
            />
            <Route path="/billing">
                <Route path="">
                    <Billing />
                </Route>
                <Route path="/cards">
                    <Cards />
                </Route>
                <Route exact path="/ad-spend-credit">
                    <Navigate to="/billing/coin-bundles" />
                </Route>
                <Route path="/coin-bundles">
                    <AdSpendCredit />
                </Route>
                <Route path="/ad-spend-credit">
                    <AdSpendCredit />
                </Route>
            </Route>

            <Route path="/recipes">
                <Route path="">
                    <Recipes />
                </Route>
                <Route path="upgrade-to-segments">
                    <RecipeUpgradeReview />
                </Route>
                <Route path="new">
                    <Navigate to="/segments/new" replace />
                </Route>
                <Route path=":recipeId">
                    <RecipeDetail />
                </Route>
            </Route>

            <Route path="/segments">
                <Route path="">
                    <Segments />
                </Route>
                <Route path=":recipeId">
                    <RecipeDetail />
                </Route>
            </Route>

            <Route path="/onboard">
                <Onboard />
            </Route>

            {/* <Route path="/mockups/onboard"><Onboard/></Route>*/}
            <Route path="/mockups/product">
                <Product />
            </Route>

            <AdminRoute>
                <Route path="/admin">
                    <Route path="/users" element={<AdminUsers />} />
                    <Route path="/users/:user_id" element={<AdminUserDetail />} />
                    <Route path="/profiles/:profile_id" element={<AdminProfileDetail />} />
                    <Route path="/bulk-actions" element={<AdminBulkActions />} />
                    <Route path="/recipes" element={<AdminRecipes />} />
                    <Route path="/public-recipes" element={<PublicRecipes />} />
                    <Route path="/sync-test" element={<RecipesTesting />} />
                    <Route path="/public-recipes/:recipeId">
                        <RecipeDetail asPublicRecipe />
                    </Route>
                    <Route path="/updates" element={<AdminUpdates />} />
                    <Route path="/updates/:update_id" element={<AdminUpdateDetails />} />
                </Route>
            </AdminRoute>
            <Route path="/undo">
                <Undo />
            </Route>
            <Route path="/sink">
                <Sink />
            </Route>
            <Route path="/sink2">
                <Sink2 />
            </Route>
            <Route path="/sink/filter">
                <SinkFilter />
            </Route>
            <Route path="/sink/numeric">
                <SinkNumeric />
            </Route>
            <Route path="/sink/audit-logs">
                <SinkAuditLogs />
            </Route>

            <Route path="/help/recipes-v2-quickstart">
                <RecipesV2Quickstart />
            </Route>

            <Route path="/help/api-docs">
                <LegacyApiDocumentationRedirect />
            </Route>

            <Route path="*" element={<NotFound />} />
        </Routes>
    )
}
